import { useEffect, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useProject, useProjectBoxes } from '../api/queries'
import { CaseLabel } from '../components/CaseLabel'
import { boxTitle } from '../components/CaseBoxCard'
import {
  LabelMediaPicker,
  LabelPageStyle,
  RollPrintHint,
  labelSheetClass,
  useLabelMedia
} from '../components/LabelPrint'
import { ErrorBox, Loading } from '../components/ui'

/**
 * Printvenlig side med mærkaterne til sagskasserne (beslutning 143 og 145) - ét pr. kasse.
 *
 * Samme opsætning som de andre mærkater (beslutning 106): Brother QL med 62 mm endeløs
 * rulle eller ark, `@page` kun her på mærkatsiden, og alt andet end mærkaterne skjules
 * ved print.
 */
export function ProjectLabelPage() {
  const { id } = useParams()
  const projectId = Number(id)
  const { data, isLoading, error } = useProject(projectId)
  const boxes = useProjectBoxes(projectId)
  const [copies, setCopies] = useState(1)
  const [skipped, setSkipped] = useState<number[]>([])
  const [media, setMedia] = useLabelMedia()

  // Titlen bliver til filnavnet, hvis nogen printer til PDF.
  useEffect(() => {
    const previous = document.title
    document.title = 'Sagskasse-mærkat'
    return () => {
      document.title = previous
    }
  }, [])

  if (isLoading || boxes.isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (boxes.error) return <ErrorBox error={boxes.error} />
  if (!data) return <ErrorBox error={new Error('Sagen findes ikke.')} />

  const all = boxes.data ?? [{ number: 1, name: null }]
  const chosen = all.filter((box) => !skipped.includes(box.number))

  return (
    <>
      <div className="row no-print" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <Link className="btn btn--sm" to={`/projects/${projectId}`}>
          Tilbage
        </Link>

        <label className="row small muted" style={{ gap: 6 }}>
          Kopier
          <input
            type="number"
            min={1}
            max={10}
            style={{ width: 70 }}
            value={copies}
            onChange={(event) => setCopies(Math.min(10, Math.max(1, Number(event.target.value) || 1)))}
          />
        </label>

        <LabelMediaPicker media={media} onChange={setMedia} />

        <button className="btn btn--primary btn--sm" disabled={chosen.length === 0} onClick={() => window.print()}>
          Print
        </button>

        <span className="tiny muted">Sæt mærkatet på kassen. Scannes det, kan man vælge bil eller værksted.</span>
      </div>

      {/* Har sagen flere kasser, vælges hvilke der skal printes - typisk kun den nye. */}
      {all.length > 1 && (
        <div className="row no-print small" style={{ gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
          {all.map((box) => (
            <label key={box.number} className="row" style={{ gap: 6, whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                style={{ width: 'auto' }}
                checked={!skipped.includes(box.number)}
                onChange={(event) =>
                  setSkipped((list) =>
                    event.target.checked ? list.filter((n) => n !== box.number) : [...list, box.number]
                  )
                }
              />
              {boxTitle(box)}
            </label>
          ))}
        </div>
      )}

      <LabelPageStyle media={media} />
      <RollPrintHint media={media} />

      <div className={labelSheetClass(media)}>
        {chosen.flatMap((box) =>
          Array.from({ length: copies }, (_, copy) => <CaseLabel key={`${box.number}-${copy}`} detail={data} box={box} />)
        )}
      </div>
    </>
  )
}

/**
 * Landingssiden for QR-koden på en sagskasse (`/s/:id` = kasse 1, `/s/:id/:box`). Sender
 * videre til sagen med kassen fremhævet; har man ikke adgang, siger sagssiden det selv.
 */
export function CaseCodeLandingPage() {
  const { id, box } = useParams()
  const projectId = Number(id)
  const number = box ? Number(box) : 1
  if (!Number.isInteger(projectId) || projectId <= 0 || !Number.isInteger(number) || number <= 0) {
    return <ErrorBox error={new Error(`"${id}" er ikke en sag herfra.`)} />
  }
  // ?kasse=<nr>: sagssiden fremhæver kassen, så montøren straks kan vælge bil eller værksted (beslutning 144).
  return <Navigate to={`/projects/${projectId}?kasse=${number}`} replace />
}
